/**
 * Create + Edit share this one component (matching the FAQ module's
 * single-form-for-create-and-edit convention) but render two genuinely
 * separate `<form>` bodies, not a unioned one — the field sets differ by
 * exactly `code`/`amountInRupees`/`currency`, which are create-only per
 * backend §13's allow-list (architecture §7.3: not just disabled on edit,
 * absent entirely).
 */
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { ConfirmModal } from '@/src/modules/components/ui/ConfirmModal';
import {
  createProductSchema,
  editProductSchema,
  type CreateProductFormInput,
  type EditProductFormInput,
} from '../schemas';
import { useCreateProduct, useEditProduct } from '../hooks';
import { listTemplates, type AdminTemplate } from '@/src/infrastructure/lib/api';
import type { AdminProductRow } from '../types';

const inputClass =
  'w-full px-3 py-2 rounded-lg bg-surface-container-lowest border border-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-electric-violet/30';
const labelClass = 'block text-[10px] uppercase tracking-widest font-bold text-on-surface-variant mb-1';

function useTemplateOptions() {
  const { data } = useQuery({
    queryKey: ['admin', 'templates', 'picker'],
    queryFn: () => listTemplates({ limit: 100 }),
    staleTime: 60_000,
  });
  return data?.items ?? [];
}

function TemplateSelect({ templates, ...props }: { templates: AdminTemplate[] } & React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...props} className={inputClass}>
      <option value="">Select a template…</option>
      {templates.map((t) => (
        <option key={t.id} value={t.id}>
          {t.name} ({t.occasionType})
        </option>
      ))}
    </select>
  );
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  product?: AdminProductRow;
  onClose: () => void;
}

export function ProductForm({ open, mode, product, onClose }: Props) {
  if (mode === 'create') {
    return <CreateProductForm open={open} onClose={onClose} />;
  }
  if (!product) return null;
  return <EditProductForm open={open} product={product} onClose={onClose} />;
}

function CreateProductForm({ open, onClose }: { open: boolean; onClose: () => void }) {
  const templates = useTemplateOptions();
  const mutation = useCreateProduct();
  const form = useForm<CreateProductFormInput>({
    resolver: zodResolver(createProductSchema),
    defaultValues: {
      code: '',
      displayName: '',
      templateId: '',
      amountInRupees: 0,
      currency: 'INR',
      description: '',
      previewImageUrl: '',
      displayOrder: 0,
    },
  });

  const submit = form.handleSubmit(async (values) => {
    await mutation.mutateAsync({
      code: values.code,
      displayName: values.displayName,
      templateId: values.templateId,
      amountInPaise: Math.round(values.amountInRupees * 100),
      currency: values.currency,
      description: values.description || undefined,
      previewImageUrl: values.previewImageUrl || undefined,
      displayOrder: values.displayOrder,
    });
    form.reset();
    onClose();
  });

  return (
    <ConfirmModal
      open={open}
      title="Create product"
      confirmLabel="Create"
      loading={mutation.isPending}
      onClose={onClose}
      onConfirm={() => void submit().catch(() => {})}
    >
      <form onSubmit={(e) => void submit(e).catch(() => {})} className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
        <div>
          <label className={labelClass}>Code (immutable once created)</label>
          <input {...form.register('code')} placeholder="ROYAL_COLLECTION" className={inputClass} />
          {form.formState.errors.code && <p className="text-xs text-red-300 mt-1">{form.formState.errors.code.message}</p>}
        </div>
        <div>
          <label className={labelClass}>Display name</label>
          <input {...form.register('displayName')} className={inputClass} />
          {form.formState.errors.displayName && <p className="text-xs text-red-300 mt-1">{form.formState.errors.displayName.message}</p>}
        </div>
        <div>
          <label className={labelClass}>Template</label>
          <TemplateSelect templates={templates} {...form.register('templateId')} />
          {form.formState.errors.templateId && <p className="text-xs text-red-300 mt-1">{form.formState.errors.templateId.message}</p>}
        </div>
        <div>
          <label className={labelClass}>Price (₹) <span className="text-[10px] text-muted-foreground font-normal">(min ₹500, ADR-019 sale floor)</span></label>
          <input type="number" min={500} step="1" {...form.register('amountInRupees', { valueAsNumber: true })} className={inputClass} />
          {form.formState.errors.amountInRupees && <p className="text-xs text-red-300 mt-1">{form.formState.errors.amountInRupees.message}</p>}
        </div>
        <div>
          <label className={labelClass}>Currency</label>
          <input value="INR" disabled className={`${inputClass} opacity-60`} />
        </div>
        <div>
          <label className={labelClass}>Description (optional)</label>
          <textarea {...form.register('description')} rows={2} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Preview image URL (optional)</label>
          <input {...form.register('previewImageUrl')} className={inputClass} />
          {form.formState.errors.previewImageUrl && <p className="text-xs text-red-300 mt-1">{form.formState.errors.previewImageUrl.message}</p>}
        </div>
        <div>
          <label className={labelClass}>Display order</label>
          <input type="number" {...form.register('displayOrder', { valueAsNumber: true })} className={inputClass} />
        </div>
        {mutation.error && <p className="text-xs text-red-300">{(mutation.error as Error).message}</p>}
      </form>
    </ConfirmModal>
  );
}

function EditProductForm({ open, product, onClose }: { open: boolean; product: AdminProductRow; onClose: () => void }) {
  const templates = useTemplateOptions();
  const mutation = useEditProduct(product.id);
  const form = useForm<EditProductFormInput>({
    resolver: zodResolver(editProductSchema),
    defaultValues: {
      displayName: product.displayName,
      templateId: product.templateId,
      description: product.description ?? '',
      previewImageUrl: product.previewImageUrl ?? '',
      displayOrder: product.displayOrder,
    },
  });

  useEffect(() => {
    form.reset({
      displayName: product.displayName,
      templateId: product.templateId,
      description: product.description ?? '',
      previewImageUrl: product.previewImageUrl ?? '',
      displayOrder: product.displayOrder,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id, open]);

  const submit = form.handleSubmit(async (values) => {
    await mutation.mutateAsync({
      displayName: values.displayName,
      templateId: values.templateId,
      description: values.description || undefined,
      previewImageUrl: values.previewImageUrl || undefined,
      displayOrder: values.displayOrder,
    });
    onClose();
  });

  return (
    <ConfirmModal
      open={open}
      title={`Edit ${product.displayName}`}
      confirmLabel="Save changes"
      loading={mutation.isPending}
      onClose={onClose}
      onConfirm={() => void submit().catch(() => {})}
    >
      {/* No `code`/`amountInPaise`/`version` field anywhere in this form — allow-list only (architecture §7.3). */}
      <form onSubmit={(e) => void submit(e).catch(() => {})} className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
        <div>
          <label className={labelClass}>Display name</label>
          <input {...form.register('displayName')} className={inputClass} />
          {form.formState.errors.displayName && <p className="text-xs text-red-300 mt-1">{form.formState.errors.displayName.message}</p>}
        </div>
        <div>
          <label className={labelClass}>Template</label>
          <TemplateSelect templates={templates} {...form.register('templateId')} />
          {form.formState.errors.templateId && <p className="text-xs text-red-300 mt-1">{form.formState.errors.templateId.message}</p>}
        </div>
        <div>
          <label className={labelClass}>Description (optional)</label>
          <textarea {...form.register('description')} rows={2} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Preview image URL (optional)</label>
          <input {...form.register('previewImageUrl')} className={inputClass} />
          {form.formState.errors.previewImageUrl && <p className="text-xs text-red-300 mt-1">{form.formState.errors.previewImageUrl.message}</p>}
        </div>
        <div>
          <label className={labelClass}>Display order</label>
          <input type="number" {...form.register('displayOrder', { valueAsNumber: true })} className={inputClass} />
        </div>
        {mutation.error && <p className="text-xs text-red-300">{(mutation.error as Error).message}</p>}
      </form>
    </ConfirmModal>
  );
}
